import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { About } from "./pages/About";
import { Feed } from "./pages/Feed";
import { SinglePost } from "./pages/SinglePost";
import { routeFromPath, type Page } from "./lib/route";
import "./index.css";

type FeedMedia = {
  local_path: string;
  media_type: string;
  cdn_url?: string;
};

export type FeedPost = {
  id: string;
  caption: string;
  permalink: string;
  timestamp: string;
  media: FeedMedia[];
};

const EMPTY_FEED: FeedPost[] = [];

// Generate URL-friendly slug from caption
const generateSlug = (caption: string, id: string): string => {
  if (!caption) return id;
  
  // Clean caption: lowercase, remove special chars, take first 5 words
  const words = caption
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special chars except spaces and hyphens
    .trim()
    .split(/\s+/)
    .slice(0, 5)
    .join('-');
  
  return `${words}-${id}`;
};

function Navigation({ currentPage, onNavigate }: { currentPage: Page; onNavigate: (page: Page) => void }) {
  const handleInternalLink = (event: MouseEvent<HTMLAnchorElement>, page: Page) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();
    onNavigate(page);
  };

  const brandLink = (
    <a
      href="/"
      onClick={(event) => handleInternalLink(event, "feed")}
      className="text-xl font-bold hover:opacity-80 transition-opacity"
      aria-label="Go to home page"
    >
      @gizemonwine
    </a>
  );

  return (
    <nav className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50" aria-label="Main navigation">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {currentPage === "post" ? <div>{brandLink}</div> : <h1>{brandLink}</h1>}
          
          {currentPage === "post" ? (
            <a
              href="/"
              onClick={(event) => handleInternalLink(event, "feed")}
              className="inline-flex items-center gap-2 font-medium text-foreground hover:text-muted-foreground transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to All Posts
            </a>
          ) : (
            <div className="flex gap-6">
              <a
                href="https://www.instagram.com/gizemonwine/"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Visit Instagram profile"
              >
                Instagram
              </a>
              <a
                href="/"
                onClick={(event) => handleInternalLink(event, "feed")}
                className={`font-medium transition-colors ${
                  currentPage === "feed" 
                    ? "text-foreground border-b-2 border-foreground" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-label="View posts"
                aria-current={currentPage === "feed" ? "page" : undefined}
              >
                Posts
              </a>
              <a
                href="/about"
                onClick={(event) => handleInternalLink(event, "about")}
                className={`font-medium transition-colors ${
                  currentPage === "about" 
                    ? "text-foreground border-b-2 border-foreground" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-label="View about page"
                aria-current={currentPage === "about" ? "page" : undefined}
              >
                About
              </a>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export function App({ initialFeed = EMPTY_FEED }: { initialFeed?: FeedPost[] }) {
  const [initialRoute] = useState(() => routeFromPath(window.location.pathname));
  const [feed, setFeed] = useState<FeedPost[]>(initialFeed);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    initialFeed.length > 0 ? "ready" : "idle",
  );
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<Page>(initialRoute.page);
  const [currentPostId, setCurrentPostId] = useState<string | null>(initialRoute.postId);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const route = routeFromPath(window.location.pathname);
      setCurrentPostId(route.postId);
      setCurrentPage(route.page);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (initialFeed.length > 0) {
      return;
    }

    let cancelled = false;
    const loadFeed = async () => {
      setStatus("loading");
      setError(null);
      try {
        const response = await fetch("/data/feed.json", { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`Failed to fetch feed: ${response.status}`);
        }
        const json = (await response.json()) as FeedPost[];
        if (!cancelled) {
          setFeed(json);
          setStatus("ready");
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        setStatus("error");
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    };
    loadFeed();
    return () => {
      cancelled = true;
    };
  }, [initialFeed.length]);

  const sortedFeed = useMemo(() => {
    return [...feed].sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1));
  }, [feed]);

  const handleNavigate = (page: Page, postId?: string) => {
    if (page === "post" && postId) {
      const post = sortedFeed.find(p => p.id === postId);
      const slug = post ? generateSlug(post.caption, post.id) : postId;
      window.history.pushState({}, "", `/posts/${slug}`);
      setCurrentPage("post");
      setCurrentPostId(postId);
    } else if (page === "about") {
      window.history.pushState({}, "", "/about");
      setCurrentPage("about");
      setCurrentPostId(null);
    } else {
      window.history.pushState({}, "", "/");
      setCurrentPage("feed");
      setCurrentPostId(null);
    }
  };

  const currentPost = useMemo(() => {
    if (currentPage === "post" && currentPostId) {
      return sortedFeed.find(p => p.id === currentPostId);
    }
    return null;
  }, [currentPage, currentPostId, sortedFeed]);

  const currentPostIndex = useMemo(() => {
    if (!currentPost) {
      return -1;
    }
    return sortedFeed.findIndex(p => p.id === currentPost.id);
  }, [currentPost, sortedFeed]);

  // Update document head for SEO when viewing a post
  useEffect(() => {
    if (currentPage === "post" && currentPost) {
      const cleanCaption = currentPost.caption ? currentPost.caption.replace(/\s+/g, ' ').trim() : "";
      const ogTitle = cleanCaption 
        ? `${cleanCaption.slice(0, 60)}${cleanCaption.length > 60 ? "..." : ""}`
        : `Post by Gizem on Wine`;
      const pageTitle = `${ogTitle} | Gizem on Wine`;
      const metaDescription = cleanCaption 
        ? cleanCaption.slice(0, 155) + (cleanCaption.length > 155 ? "..." : "")
        : "Wine tasting notes and thoughts from years spent chasing bottles across Europe.";
      const firstImage = currentPost.media[0]?.cdn_url || currentPost.media[0]?.local_path || "";
      const fullImageUrl = firstImage.startsWith("http") ? firstImage : `https://gizemon.wine${firstImage}`;
      const slug = generateSlug(currentPost.caption, currentPost.id);
      const postUrl = `https://gizemon.wine/posts/${slug}`;

      // Update title
      document.title = pageTitle;

      // Update or create meta tags
      const updateMetaTag = (property: string, content: string, isProperty = true) => {
        const attr = isProperty ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attr}="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      updateMetaTag('description', metaDescription, false);
      updateMetaTag('og:title', ogTitle);
      updateMetaTag('og:description', metaDescription);
      updateMetaTag('og:url', postUrl);
      updateMetaTag('og:image', fullImageUrl);
      updateMetaTag('og:type', 'article');
      updateMetaTag('article:published_time', new Date(currentPost.timestamp).toISOString());
      updateMetaTag('article:modified_time', new Date(currentPost.timestamp).toISOString());
      updateMetaTag('twitter:card', 'summary_large_image', false);
      updateMetaTag('twitter:title', ogTitle, false);
      updateMetaTag('twitter:description', metaDescription, false);
      updateMetaTag('twitter:image', fullImageUrl, false);

      // Update canonical link
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = postUrl;

    } else if (currentPage === "feed") {
      // Reset to default home page meta
      document.title = "Gizem on Wine | Wine Tasting Notes";
      const updateMetaTag = (property: string, content: string, isProperty = true) => {
        const attr = isProperty ? 'property' : 'name';
        const meta = document.querySelector(`meta[${attr}="${property}"]`);
        if (meta) {
          meta.setAttribute('content', content);
        }
      };
      updateMetaTag('description', "Tasting notes, field impressions, and thoughts from years spent chasing bottles across Europe. Studying character, tension, texture, and emotion in wine.", false);
      updateMetaTag('og:title', "Gizem on Wine | Wine Tasting Notes");
      updateMetaTag('og:description', "Tasting notes, field impressions, and thoughts from years spent chasing bottles across Europe.");
      updateMetaTag('og:url', "https://gizemon.wine/");
      updateMetaTag('og:type', 'website');
      
      const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonical) {
        canonical.href = "https://gizemon.wine/";
      }
    } else if (currentPage === "about") {
      const aboutUrl = "https://gizemon.wine/about";
      const aboutDescription = "Learn about Gizem's path through wine, her tasting perspective, and how to get in touch.";
      document.title = "About | Gizem on Wine";

      const updateMetaTag = (property: string, content: string, isProperty = true) => {
        const attr = isProperty ? "property" : "name";
        const meta = document.querySelector(`meta[${attr}="${property}"]`);
        if (meta) {
          meta.setAttribute("content", content);
        }
      };

      updateMetaTag("description", aboutDescription, false);
      updateMetaTag("og:title", "About | Gizem on Wine");
      updateMetaTag("og:description", aboutDescription);
      updateMetaTag("og:url", aboutUrl);
      updateMetaTag("og:type", "profile");

      const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonical) {
        canonical.href = aboutUrl;
      }
    }
  }, [currentPage, currentPost]);

  return (
    <div className="min-h-screen">
      <Navigation currentPage={currentPage} onNavigate={(page) => handleNavigate(page)} />
      
      <main className="container mx-auto px-4 py-8">
        {currentPage === "about" && <About />}
        
        {currentPage === "post" && status === "ready" && currentPost && (
          <SinglePost
            post={currentPost}
            newerPost={currentPostIndex > 0 ? sortedFeed[currentPostIndex - 1] : undefined}
            olderPost={currentPostIndex >= 0 ? sortedFeed[currentPostIndex + 1] : undefined}
          />
        )}
        
        {currentPage === "post" && status === "ready" && !currentPost && (
          <div className="text-center py-16 text-muted-foreground">
            Post not found.{" "}
            <button
              onClick={() => handleNavigate("feed")}
              className="text-foreground underline hover:no-underline"
            >
              Go back to feed
            </button>
          </div>
        )}
        
        {currentPage === "feed" && (
          <>
            {status === "loading" && (
              <div className="text-center py-16 text-muted-foreground">
                Loading posts...
              </div>
            )}
            
            {status === "error" && (
              <div className="text-center py-16 text-destructive">
                Could not load feed{error ? `: ${error}` : ""}. Check that feed.json exists.
              </div>
            )}
            
            {status === "ready" && sortedFeed.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                No posts found. Run the scraper to populate the feed.
              </div>
            )}
            
            {status === "ready" && sortedFeed.length > 0 && (
              <Feed posts={sortedFeed} onPostClick={(postId) => handleNavigate("post", postId)} />
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
